import { CommonModule } from '@angular/common';
import {
  Component, OnInit, OnDestroy, inject, signal,
} from '@angular/core';
import {
  ActivatedRoute, Data, NavigationEnd, Router, RouterModule,
} from '@angular/router';

import {
  AuthService, COMPOSER_BASE_HREF, ENVIRONMENT, User, UserRole, hasComposerAccess,
} from '@instaclause-webapp/shared-core';
import { HelpHubComponent, TooltipDirective } from '@instaclause-webapp/shared-ui';
import { InlineSvgComponent } from '@instaclause-webapp/shared-ui';
import { TranslocoModule } from '@jsverse/transloco';
import { filter, Subscription } from 'rxjs';

import { LanguageComponent } from '../language/language.component';

interface Breadcrumb {
  /** Resolved names (contract, customer) that are not translated. */
  title?: string;
  titleKey?: string;
  path: string;
}

type RouteBreadcrumb = Breadcrumb | ((data: Data) => Breadcrumb | undefined);

@Component({
  selector: 'app-menu',
  imports: [
    CommonModule,
    TranslocoModule,
    LanguageComponent,
    InlineSvgComponent,
    RouterModule,
    TooltipDirective,
    HelpHubComponent,
  ],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss',
  providers: [],
})
export class MenuComponent implements OnInit, OnDestroy {
  user!: User | null;

  breadcrumb = signal<Breadcrumb[]>([]);
  contractName = signal<string | undefined>(undefined);
  currentPage = signal<string>('');

  appVersion = inject(ENVIRONMENT).version;
  composerUrl = COMPOSER_BASE_HREF;

  private authService = inject(AuthService);
  private subscription = new Subscription();
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  /** Settings and Customers are office-scoped and fail for a user without one. */
  get hasOffice(): boolean {
    return !!this.user?.customerId;
  }

  get isAdmin(): boolean {
    return this.user?.userRole === UserRole.Admin && this.hasOffice;
  }

  get canAccessComposer(): boolean {
    return hasComposerAccess(this.user);
  }

  constructor() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.breadcrumb.set(this.getBreadcrumbs(this.activatedRoute));
    });
  }


  ngOnInit(): void {
    this.subscription.add(this.authService.user$.subscribe((user) => (this.user = user)));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  async logout(): Promise<void> {
    await this.authService.logout();
  }

  getBreadcrumbs(activatedRoute: ActivatedRoute): Breadcrumb[] {
    const breadcrumbs: any[] = [];

    const { data } = activatedRoute.snapshot;
    const breadcrumb: RouteBreadcrumb | undefined = data['breadcrumb'];

    const crumb = typeof breadcrumb === 'function' ? breadcrumb(data) : breadcrumb;

    if (crumb) {
      breadcrumbs.push(crumb);
    }

    if (activatedRoute.firstChild) {
      return [ ...new Set([ ...breadcrumbs,
        ...this.getBreadcrumbs(activatedRoute.firstChild) ]) ];
    }

    return [ ...new Set(breadcrumbs || []) ];
  }
}
